import { useEffect, useRef, useState } from 'react';
import { storedWalletOutcome, walletRequestRows } from './agentWithdrawalRecovery';
export const agentPendingKey = (api, identity) => identity ? `veritason-pending:agent:${api || window.location.origin}:${identity}` : null;
const read = key => {
    if (!key) throw new Error('영업자 정보를 확인하지 못했습니다.');
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (typeof p?.requestId !== 'string' || !p.requestId.trim()) throw new Error('저장된 송금 요청을 확인하지 못했습니다.');
    return { requestId:p.requestId, amount:Number.isSafeInteger(p.amount)?p.amount:null, fee:Number.isSafeInteger(p.fee)?p.fee:null, bankName:typeof p.bankName==='string'?p.bankName:'', accountLast4:typeof p.accountLast4==='string'?p.accountLast4.slice(-4):'', createdAt:typeof p.createdAt==='string'?p.createdAt:'' };
};
export default function useAgentRecovery({ enabled, identity, api, onQuery, onList, onRefresh }) {
    const key = agentPendingKey(api, identity);
    const [pending,setPending]=useState(null), [ready,setReady]=useState(false), [busy,setBusy]=useState(false);
    const [message,setMessage]=useState(''), [terminal,setTerminal]=useState(false);
    const activeRequest=useRef(null);
    const lock=useRef(false), generation=useRef(0), alive=useRef(false);
    const callbacks=useRef({onQuery,onList,onRefresh});callbacks.current={onQuery,onList,onRefresh};
    const refresh=()=>Promise.resolve().then(()=>callbacks.current.onRefresh?.()).catch(()=>{});
    const finish=(p,outcome)=>{
        setTerminal(true);setMessage(outcome.message);
        try { if(read(key)?.requestId===p.requestId)localStorage.removeItem(key); }
        catch { setMessage(`${outcome.message} 저장된 요청키를 정리하지 못했습니다. 결과를 다시 조회해주세요.`); }
        void refresh();
    };
    const query=async saved=>{
        if(lock.current)return;
        lock.current=true;setBusy(true);const gen=generation.current;
        try {
            let p=saved || read(key);setReady(false);setTerminal(false);
            if(!p){
                const rows=walletRequestRows(await callbacks.current.onList());
                if(!alive.current||gen!==generation.current)return;
                if(rows.length){
                    const first=rows[0];
                    p={requestId:first.request_id,amount:first.amount,fee:first.withdrawal_fee};
                    localStorage.setItem(key,JSON.stringify(p));
                }
            }
            setPending(p);
            if(!p){setReady(true);setMessage('');return;}
            const rows=walletRequestRows(await callbacks.current.onQuery({request_id:p.requestId}));
            if(!alive.current||gen!==generation.current)return;
            const matches=rows.filter(r=>r.request_id===p.requestId);
            const outcome=storedWalletOutcome(matches.length===1?matches[0]:null);
            if(outcome.terminal)finish(p,outcome);
            else setMessage(matches.length===0?'요청이 아직 조회되지 않습니다. 새로 송금하지 말고 같은 요청으로 다시 확인해주세요.':outcome.message);
        } catch { if(alive.current&&gen===generation.current){setReady(false);setMessage('송금 결과를 조회하지 못했습니다. 기존 요청키를 유지하고 다시 확인해주세요.');} }
        finally {if(gen===generation.current){lock.current=false;if(alive.current)setBusy(false);}}
    };
    const queryRef=useRef(query);queryRef.current=query;
    useEffect(()=>{
        alive.current=true;activeRequest.current=null;setPending(null);setReady(!enabled);setMessage('');setTerminal(false);lock.current=false;
        if(enabled)void queryRef.current();
        const storage=e=>{if(enabled&&(e.key===key||e.key===null)&&!lock.current)void queryRef.current();};
        window.addEventListener('storage',storage);
        return()=>{alive.current=false;generation.current+=1;lock.current=false;window.removeEventListener('storage',storage);};
    },[key,enabled]);
    const reserve=async confirmation=>{
        if(!enabled)return;
        if(!ready||lock.current||terminal||pending)throw new Error('이전 송금 결과를 먼저 확인해주세요.');
        const gen=generation.current;
        const claim=()=>{
            if(!alive.current||gen!==generation.current)throw new Error('송금 화면이 변경되었습니다.');
            const existing=read(key);
            if(existing){setPending(existing);setReady(false);throw new Error('확인되지 않은 송금 요청이 있습니다.');}
            const p={requestId:confirmation.requestId,amount:confirmation.amount,fee:confirmation.fee,
                bankName:confirmation.recipient.bankName,accountLast4:confirmation.recipient.account.slice(-4),createdAt:new Date().toISOString()};
            localStorage.setItem(key,JSON.stringify(p)); // Must succeed before POST. No credentials or full destination persisted.
            activeRequest.current={requestId:p.requestId,generation:gen};
            setPending(p);setMessage('송금 접수 결과를 확인하고 있습니다.');setReady(false);lock.current=true;setBusy(true);
        };
        try {if(navigator.locks?.request)await navigator.locks.request(key,claim);else claim();if(!alive.current||gen!==generation.current)throw new Error('송금 화면이 변경되었습니다.');}
        catch(e){if(alive.current&&gen===generation.current)setMessage('요청키를 보관하지 못했거나 기존 요청이 있어 송금을 중단했습니다.');throw e;}
    };
    const settled=(confirmation,result)=>{
        if(!enabled||!alive.current||activeRequest.current?.generation!==generation.current||activeRequest.current?.requestId!==confirmation.requestId)return;
        lock.current=false;setBusy(false);
        const p={requestId:confirmation.requestId};
        if(result?.code===1000)finish(p,{terminal:true,message:'송금이 완료되었습니다.'});
        else if(result?.code===1002)finish(p,{terminal:true,message:result.msg||'송금이 실패로 확정되었습니다.'});
        else setMessage(result?.code===1001?'송금 처리 중입니다.':result?.code===1003?'송금 결과 확인 대기 중입니다.':result?.msg||'송금 결과가 미확정입니다. 같은 요청 번호로 결과를 조회해주세요.');
        void refresh();
    };
    return {pending,ready,busy,message,terminal,reserve,settled,query:()=>query(pending),restart:()=>query(),blocked:enabled&&!ready};
}
